import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, PackageCheck, Truck, X, Check, TriangleAlert, ChevronDown } from "lucide-react";

const REQ_STATUS = {
  pending: { label: "Onay Bekliyor", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Onaylı · Yükleme Bekliyor", cls: "bg-sky-100 text-sky-800" },
  loaded: { label: "Yüklendi", cls: "bg-emerald-100 text-emerald-800" },
  loaded_partial: { label: "Kısmi Yüklendi", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-700" },
};

export default function VanStockApprovals() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [appr, setAppr] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/van-sales/stock-requests", { params }).then((r) => setRows(r.data || [])).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null);
    try {
      const { data } = await api.get(`/van-sales/stock-requests/${id}`);
      setDetail(data);
      const m = {}; (data.lines || []).forEach((l) => { m[l.product_id] = l.approved_qty != null ? l.approved_qty : l.quantity; });
      setAppr(m);
    } catch { toast.error("Detay alınamadı"); }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/van-sales/stock-requests/${id}/approve`, {
        lines: Object.entries(appr).map(([product_id, approved_qty]) => ({ product_id, approved_qty: Number(approved_qty) || 0 })),
      });
      toast.success("Talep onaylandı — depo hazırlayabilir"); setOpenId(null); setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Onaylanamadı"); }
    finally { setBusy(false); }
  };
  const reject = async (id) => {
    setBusy(true);
    try { await api.post(`/van-sales/stock-requests/${id}/reject`, {}); toast.success("Talep reddedildi"); setOpenId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reddedilemedi"); }
    finally { setBusy(false); }
  };
  const doLoad = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/van-sales/stock-requests/${id}/load`);
      if ((data.shortages || []).length) toast.warning(`Yüklendi ama ${data.shortages.length} kalem stok yetersiz`);
      else toast.success("Onaylı stok araca yüklendi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Yüklenemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="van-stock-approvals">
      <div className="flex flex-wrap gap-2">
        {[["pending", "Onay Bekleyen"], ["approved", "Hazırlanan/Yüklenecek"], ["loaded", "Yüklenen"], ["all", "Tümü"]].map(([v, l]) => (
          <button key={v} data-testid={`stockappr-filter-${v}`} onClick={() => setFilter(v)} className={`rounded-full px-3 py-1 text-xs font-medium ${filter === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
      {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-400" data-testid="stockappr-empty">Kayıt yok.</div>}
      <div className="space-y-2">
        {rows.map((r) => {
          const s = REQ_STATUS[r.status] || REQ_STATUS.pending;
          return (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`stockappr-item-${r.id}`}>
              <button onClick={() => open(r.id)} className="flex w-full items-center justify-between px-4 py-3 text-left">
                <div>
                  <div className="text-sm font-medium text-slate-800">{r.driver_name} · {r.vehicle_plate} {r.urgency === "urgent" && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">ACİL</span>}</div>
                  <div className="text-xs text-slate-500">{(r.lines || []).length} kalem · gereken: {r.need_date}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>{s.label}</span>
                  {r.status === "approved" && <Button size="sm" data-testid={`stockappr-load-${r.id}`} onClick={(e) => { e.stopPropagation(); doLoad(r.id); }} disabled={busy}><Truck className="mr-1 h-3.5 w-3.5" /> Yükle</Button>}
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openId === r.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {openId === r.id && (
                <div className="border-t border-slate-100 px-4 py-3" data-testid={`stockappr-detail-${r.id}`}>
                  {!detail ? <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-300" /></div> : (
                    <>
                      <table className="w-full text-sm">
                        <thead className="text-left text-[11px] uppercase text-slate-400"><tr><th className="py-1">Ürün</th><th className="text-right">İstenen</th><th className="text-right">Depo</th><th className="text-right">Onaylanan</th></tr></thead>
                        <tbody>
                          {(detail.lines || []).map((l) => (
                            <tr key={l.product_id} className="border-t border-slate-50">
                              <td className="py-1.5 text-slate-700">{l.name}
                                {l.insufficient && <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-red-100 px-1 text-[10px] text-red-700"><TriangleAlert className="h-2.5 w-2.5" />yetersiz</span>}
                                {!l.insufficient && l.low_after && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">depo kritik</span>}
                              </td>
                              <td className="text-right text-slate-500">{l.quantity} {l.unit}</td>
                              <td className={`text-right ${l.insufficient ? "font-semibold text-red-600" : "text-slate-500"}`}>{l.wh_available ?? "—"}</td>
                              <td className="text-right">
                                {r.status === "pending" ? (
                                  <Input type="number" min="0" step="1" value={appr[l.product_id] ?? ""} onChange={(e) => setAppr({ ...appr, [l.product_id]: e.target.value })} className="ml-auto h-7 w-20 text-right" data-testid={`stockappr-qty-${l.product_id}`} />
                                ) : <span className="font-medium text-slate-700">{l.loaded_qty > 0 ? l.loaded_qty : l.approved_qty}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {r.note && <p className="mt-2 text-xs text-slate-500">Not: {r.note}</p>}
                      {r.status === "pending" && (
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" onClick={() => approve(r.id)} disabled={busy} data-testid={`stockappr-approve-${r.id}`}><Check className="mr-1 h-4 w-4" /> Onayla</Button>
                          <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => reject(r.id)} disabled={busy} data-testid={`stockappr-reject-${r.id}`}><X className="mr-1 h-4 w-4" /> Reddet</Button>
                        </div>
                      )}
                      {r.status === "approved" && <div className="mt-3"><Button size="sm" onClick={() => doLoad(r.id)} disabled={busy}><Truck className="mr-1 h-4 w-4" /> Araca Yükle</Button></div>}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-400"><PackageCheck className="h-3.5 w-3.5" /> Onaylanan adet araca yüklenince depo stoğundan düşülür ve araç defterine işlenir.</div>
    </div>
  );
}
